<template>
  <section class="testimonials-section">
    <div class="container">
      <span class="section-tag">TESTIMONIALS</span>
      <h2 class="section-title">What Our Clients Say</h2>
      <p class="section-subtitle">
        Real stories from homeowners and investors who found their perfect
        property with Clients Preferred Property Hub.
      </p>

      <div class="carousel-container">
        <button
          class="nav-btn prev"
          @click="prevSlide"
          aria-label="Previous slide"
        >
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            stroke="currentColor"
            stroke-width="2.5"
            fill="none"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>

        <button class="nav-btn next" @click="nextSlide" aria-label="Next slide">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            stroke="currentColor"
            stroke-width="2.5"
            fill="none"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>

        <div class="carousel-viewport">
          <div
            class="carousel-track"
            :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
          >
            <div
              v-for="(testimonial, index) in testimonials"
              :key="index"
              class="testimonial-slide"
            >
              <div class="testimonial-card">
                <div class="quote-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="60"
                    height="60"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"
                    />
                  </svg>
                </div>

                <div class="content-wrapper">
                  <p class="review-text">"{{ testimonial.text }}"</p>
                  <div class="highlights" v-if="testimonial.highlights">
                    <span
                      v-for="tag in testimonial.highlights"
                      :key="tag"
                      class="tag"
                    >
                      {{ tag }}
                    </span>
                  </div>
                </div>

                <div class="client-info">
                  <div class="client-avatar">
                    {{ testimonial.name.charAt(0) }}
                  </div>
                  <div class="client-details">
                    <h4>{{ testimonial.name }}</h4>
                    <p class="verified">Verified Client</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="carousel-dots">
          <button
            v-for="(_, index) in testimonials"
            :key="index"
            class="dot"
            :class="{ active: currentIndex === index }"
            @click="goToSlide(index)"
          ></button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue';

const testimonials = [
  {
    name: 'Raj',
    text: 'I had a very positive experience throughout my property purchase. I received a genuine deal with an excellent discount, which made the investment even more worthwhile. The team was extremely supportive in assisting me with the home loan process, guiding me at every step and ensuring everything was handled smoothly. What I appreciated the most was their commitment and dedication—they stood by me from the initial booking all the way through to the final delivery of my flat. Their professionalism, transparency, and consistent support made the entire journey stress-free and satisfying.',
    highlights: ['Home Loan Support', 'Genuine Deal', 'Full Delivery Support'],
  },
  {
    name: 'Vikas',
    text: 'My budget for a 2BHK was around 25 lakhs, and I was quite particular about finding a property that met both my financial limits and quality expectations. They patiently understood my needs and showed me a well-planned 2BHK flat located on the Kharar–Kurali Road in a gated society, which perfectly matched my expectations. I received a genuine deal with a good discount, and the team also extended full support in processing my home loan. Their honesty and dedication made my home-buying journey smooth and stress-free.',
    highlights: ['Budget Friendly', 'Gated Society', 'Transparent Guidance'],
  },
  {
    name: 'Vikram',
    text: 'I was looking for a property around ₹1.30 Cr for investment and residential purposes. Traveling from Bangalore to Chandigarh, I met the team at Clients Preferred. They were extremely professional and educated me about potential fraud risks in certain areas. They presented me with villas in a RERA-approved gated society, explaining the infrastructure and future growth prospects. After reviewing everything, I finalized the decision. I truly appreciate their transparency, market knowledge, and end-to-end support.',
    highlights: ['Investment Expert', 'RERA Approved', 'Outstation Support'],
  },
];

const currentIndex = ref(0);

const nextSlide = () => {
  currentIndex.value = (currentIndex.value + 1) % testimonials.length;
};

const prevSlide = () => {
  currentIndex.value =
    (currentIndex.value - 1 + testimonials.length) % testimonials.length;
};

const goToSlide = (index) => {
  currentIndex.value = index;
};
</script>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 10px;
}

.testimonials-section {
  padding: 10px 0;
  background: #eeeeee;
  text-align: center;
}

.section-tag {
  color: #b91c1c;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-size: 0.9rem;
}

.section-title {
  font-size: 3rem;
  margin: 10px 0 15px;
  color: #111;
  font-weight: 800;
}

.section-subtitle {
  color: #666;
  max-width: 700px;
  margin: 0 auto 30px;
  line-height: 1.6;
  font-size: 1.1rem;
}

/* CAROUSEL CONTAINER */
.carousel-container {
  position: relative;
  max-width: 1050px;
  margin: 0 auto;

  padding: 0;
}

.carousel-viewport {
  overflow: hidden;
  border-radius: 30px;
  background: #ededed;
}

.carousel-track {
  display: flex;
  transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1);
}

.testimonial-slide {
  min-width: 100%;
  box-sizing: border-box;
}

.testimonial-card {
  padding: 60px 80px; /* Generous padding */
  text-align: left;
  display: flex;
  flex-direction: column;
  min-height: 480px; /* Increased to ensure names are visible */
}

/* NAVIGATION BUTTONS */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  border: none;
}

.prev {
  left: -25px;
  background: #fff;
  color: #b91c1c;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.next {
  right: -25px;
  background: #b91c1c; /* Red background for next as per image */
  color: #fff;
}

.nav-btn:hover {
  transform: translateY(-50%) scale(1.1);
}

/* PAGINATION DOTS */
.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 40px;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ddd;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot.active {
  background: #b91c1c;
  width: 30px; /* Stylish elongated active dot */
  border-radius: 10px;
}

/* CARD INTERNALS */
.quote-icon {
  color: #b91c1c;
  opacity: 0.1;
  margin-bottom: 10px;
}

.review-text {
  font-size: 1.2rem;
  color: #333;
  line-height: 1.8;
  font-style: italic;
  margin-bottom: 30px;
}

.highlights {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 40px;
}

.tag {
  background: #fff;
  color: #b91c1c;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 6px 16px;
  border-radius: 100px;
  border: 1px solid #b91c1c22;
  text-transform: uppercase;
}

.client-info {
  display: flex;
  align-items: center;
  gap: 20px;
  padding-top: 30px;
  border-top: 1px solid #e0e0e0;
  margin-top: auto; /* Pushes info to the bottom */
}

.client-avatar {
  width: 55px;
  height: 55px;
  background: #b91c1c;
  color: white;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1.4rem;
}

.client-details h4 {
  margin: 0;
  color: #111;
  font-size: 1.2rem;
  font-weight: 700;
}

.verified {
  margin: 0;
  font-size: 0.9rem;
  color: #059669;
  font-weight: 600;
}

@media (max-width: 768px) {
  .testimonial-card {
    padding: 40px 30px;
    min-height: 550px;
  }
  .section-title {
    font-size: 2.2rem;
  }
  .nav-btn {
    width: 40px;
    height: 40px;
  }
  .prev {
    left: -10px;
  }
  .next {
    right: -10px;
  }
}
</style>
